"use client";

import type { SseFrame } from "@/shared/builder-session";
import {
  consumeSse,
  flowopsApiUrl,
  flowopsFetch,
  readResponseError,
} from "@/shared/builder-session";
import { recordDevTraceFrame } from "@/features/dev-trace/dev-trace-store";
import { applyChatFrame } from "@/features/chat/multi/signals/chat-signals";
import { dispatchSessionFrame } from "@/features/chat/multi/transport/session-stream-paint";
import { subscribeAgentRuntimeSession } from "@/features/chat/multi/transport/agent-runtime-stream";
import {
  DEFAULT_RESUME_MESSAGE,
  resumeAutonomousBuild,
  shouldAutoResumeAfterRestore,
} from "@/features/chat/builder/sync/resume-autonomous-build";
import { setChatLive } from "@/features/chat/multi/history/chat-history";
import { setChatAwaitingInput } from "@/features/chat/multi/signals/chat-signals";
import { armSidebarAwaitingFromSession } from "@/features/chat/multi/signals/arm-sidebar-awaiting";

/**
 * FlowOps has no `/api/workflow/events` chat-events bus.
 * Transport is durable agent-runtime (`/api/agent-runtime/stream`) only —
 * do not probe events (that 404 was expected noise).
 */
export function ensureMultiplexConnection(): Promise<void> {
  return Promise.resolve();
}

export function isDurableTransportMode(): boolean {
  return true;
}

function sessionIdFromBody(body: Record<string, unknown>): string {
  return typeof body.sessionId === "string" ? body.sessionId.trim() : "";
}

function isTerminalEvent(event: string): boolean {
  return event === "run-end" || event === "stream-end" || event === "stream-ended";
}

function paintFrame(chatId: string, frame: SseFrame): void {
  applyChatFrame(chatId, frame);
  recordDevTraceFrame({
    chatId,
    event: frame.event,
    data: frame.data,
  });
}

/** Session-lifetime agent-runtime hubs (FlowOps subscribeSession parity). */
const durableSessionHubs = new Map<string, () => void>();

/** Focused builder owns FlowOps keep-chat snapshot apply on resync. */
type SessionResyncOutcome = "resume" | "done" | "gone";

const sessionResyncHandlers = new Map<string, () => Promise<SessionResyncOutcome>>();

/** Session id whose autonomous resume waits for the hub to re-attach (Ajai). */
let resumeAfterResubscribe: string | null = null;

export function registerSessionResyncHandler(
  sessionId: string,
  resync: () => Promise<SessionResyncOutcome>,
): () => void {
  const id = sessionId.trim();
  if (!id) return () => {};
  sessionResyncHandlers.set(id, resync);
  return () => {
    if (sessionResyncHandlers.get(id) === resync) sessionResyncHandlers.delete(id);
  };
}

function deliverDurableHubFrame(sessionId: string, frame: SseFrame): void {
  const next: SseFrame = isTerminalEvent(frame.event)
    ? { event: "stream-ended", data: frame.data }
    : frame;
  paintFrame(sessionId, next);
  dispatchSessionFrame(sessionId, next);
}

function kickOffOrphanResume(sessionId: string): void {
  void resumeAutonomousBuild({
    sessionId,
    message: DEFAULT_RESUME_MESSAGE,
    stream: async (url, body) => {
      await runDurableWorkflow({
        url,
        body,
        signal: new AbortController().signal,
        onFrame: (frame) => {
          paintFrame(sessionId, frame);
          dispatchSessionFrame(sessionId, frame);
        },
      });
    },
  }).catch((error) => {
    console.warn("[multiplex] Deferred orphan resume failed", error);
  });
}

/**
 * FlowOps `restoreActiveBuildSession` mid-build continue — start immediately and
 * keep the durable hub attached for catch-up frames.
 */
export function resumeBuildAfterRestore(sessionId: string): void {
  const id = sessionId.trim();
  if (!id) return;
  ensureDurableSessionHub(id);
  kickOffOrphanResume(id);
}

/**
 * FlowOps `resyncBuildSession` — GET + keep-chat apply (focused) or resume flag only.
 */
async function resyncDurableSession(sessionId: string): Promise<void> {
  const id = sessionId.trim();
  if (!id) return;

  const focused = sessionResyncHandlers.get(id);
  if (focused) {
    try {
      const outcome = await focused();
      if (outcome === "gone") {
        releaseDurableSessionHub(id);
        return;
      }
      if (outcome === "resume") {
        resumeAfterResubscribe = id;
      }
    } catch (error) {
      console.warn("[multiplex] Focused session resync failed", error);
    }
    return;
  }

  // Background chat: schedule orphan resume from snapshot flags only.
  try {
    const res = await flowopsFetch(`/api/workflow/session/${encodeURIComponent(id)}`);
    if (res.status === 404) {
      releaseDurableSessionHub(id);
      return;
    }
    if (!res.ok) return;
    const snapshot = (await res.json()) as {
      status?: string;
      runActive?: boolean;
      isRunning?: boolean;
      pendingUserQuestion?: unknown;
      pendingComposioExecApprovals?: unknown[];
      pendingComposioApprovals?: unknown[];
      pendingRuntimeInputRequests?: unknown;
    };
    if (snapshot.runActive || snapshot.isRunning) return;
    const composioApprovals =
      snapshot.pendingComposioExecApprovals ?? snapshot.pendingComposioApprovals;
    const hasPendingHitl = Boolean(
      snapshot.pendingUserQuestion ||
      (Array.isArray(composioApprovals) && composioApprovals.length > 0) ||
      snapshot.pendingRuntimeInputRequests,
    );
    if (hasPendingHitl) {
      // Background chat: no painter to apply the card — still arm sidebar yellow.
      setChatAwaitingInput(id, true);
      setChatLive(id, false);
      return;
    }
    const phase = String(snapshot.status ?? "");
    if (
      phase === "awaiting_approval" ||
      phase === "awaiting_intent_approval" ||
      phase === "paused"
    ) {
      armSidebarAwaitingFromSession(id);
      return;
    }
    if (shouldAutoResumeAfterRestore(phase, hasPendingHitl)) {
      resumeAfterResubscribe = id;
    }
  } catch {
    // Resync is best-effort before reconnect.
  }
}

/**
 * Keep one hub listener per session across mutation turns. Incomplete-phase
 * continue emits `run-end` then more `node-added` — unsubscribing drops the canvas.
 *
 * Lifecycle matches Ajai FlowOps: resync-before-reattach, deferred orphan resume
 * after `ready` / onSubscribed, reconnect status for the composer banner.
 */
export function ensureDurableSessionHub(sessionId: string): void {
  const id = sessionId.trim();
  if (!id || durableSessionHubs.has(id)) return;
  const unsubscribe = subscribeAgentRuntimeSession(
    id,
    (frame) => {
      deliverDurableHubFrame(id, frame);
    },
    {
      onSubscribed: (sid) => {
        if (resumeAfterResubscribe !== sid) return;
        resumeAfterResubscribe = null;
        kickOffOrphanResume(sid);
      },
      onSubscribeResync: (sid) => resyncDurableSession(sid),
    },
  );
  durableSessionHubs.set(id, unsubscribe);
}

export function releaseDurableSessionHub(sessionId: string | null | undefined): void {
  const id = sessionId?.trim();
  if (!id) return;
  durableSessionHubs.get(id)?.();
  durableSessionHubs.delete(id);
}

export function releaseAllDurableSessionHubs(): void {
  for (const unsubscribe of durableSessionHubs.values()) unsubscribe();
  durableSessionHubs.clear();
}

export function isDurableSessionHubOpen(sessionId: string | null | undefined): boolean {
  const id = sessionId?.trim();
  return Boolean(id && durableSessionHubs.has(id));
}

/**
 * Current FlowOps protocol: mutation returns JSON `{ sessionId, runId }`;
 * chunks arrive on the long-lived `/api/agent-runtime/stream` connection.
 * Resolve after JSON — hub stays session-scoped (do not wait for run-end).
 */
async function runDurableWorkflow(options: {
  url: string;
  body: Record<string, unknown>;
  signal: AbortSignal;
  onFrame: (frame: SseFrame) => void;
  onActivity?: () => void;
}): Promise<void> {
  if (options.signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  let chatId = sessionIdFromBody(options.body);

  // Known session: parent already claimPainter'd — subscribe-first so early
  // hub events are not missed. Brand-new starts attach after session frame.
  if (chatId) ensureDurableSessionHub(chatId);

  const response = await fetch(flowopsApiUrl(options.url), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/event-stream",
    },
    body: JSON.stringify(options.body),
    cache: "no-store",
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(await readResponseError(response));
  }

  const contentType = response.headers.get("content-type") ?? "";
  // Legacy peers may still stream SSE on the mutation itself.
  if (contentType.includes("text/event-stream")) {
    await consumeSse(
      response,
      (frame) => {
        options.onActivity?.();
        if (frame.event === "session" && frame.data && typeof frame.data === "object") {
          const nextId = (frame.data as { sessionId?: string }).sessionId?.trim();
          if (nextId) {
            chatId = nextId;
            options.onFrame(frame);
            ensureDurableSessionHub(nextId);
            return;
          }
        }
        if (chatId) {
          paintFrame(chatId, frame);
          dispatchSessionFrame(chatId, frame);
        } else {
          options.onFrame(frame);
        }
      },
      options.signal,
    );
    return;
  }

  const payload = (await response.json().catch(() => null)) as {
    sessionId?: unknown;
    runId?: unknown;
    error?: unknown;
  } | null;

  if (payload && typeof payload.error === "string" && payload.error.trim()) {
    throw new Error(payload.error);
  }

  const nextSessionId =
    (typeof payload?.sessionId === "string" ? payload.sessionId.trim() : "") || chatId;
  if (!nextSessionId) {
    throw new Error("Durable turn missing sessionId");
  }

  const runId =
    typeof payload?.runId === "string" && payload.runId.trim() ? payload.runId.trim() : undefined;

  chatId = nextSessionId;

  // Claim painter BEFORE hub attach so ring-buffer replay is not dropped.
  options.onFrame({
    event: "session",
    data: { sessionId: nextSessionId, ...(runId ? { runId } : {}) },
  });
  options.onFrame({ event: "stream-start", data: {} });
  ensureDurableSessionHub(nextSessionId);
  // Hub delivers run-end → stream-ended and all node-added frames from here.
}

/** Public entry — FlowOps always uses the durable agent-runtime path. */
export async function runMultiplexWorkflow(options: {
  url: string;
  body: Record<string, unknown>;
  signal: AbortSignal;
  onFrame: (frame: SseFrame) => void;
  /** Fires for every event of this run, including internal ones that never render. */
  onActivity?: () => void;
}): Promise<void> {
  return runDurableWorkflow(options);
}
